import { computed, inject } from '@angular/core'

import {
  patchState,
  signalStore,
  withComputed,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals'

import { lastValueFrom } from 'rxjs'

import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'

import { NotificationsCenterApi } from './notifications-center.api'
import {
  DEFAULT_NOTIFICATION_FILTER,
  MIN_SEARCH_LENGTH,
  NotificationFilter,
  searchAndFilterNotifications,
} from './notifications-center.model'

import { ExtendedNotification, extendNotification } from '../notifications.model'

export interface NotificationsCenterState {
  notifications: ExtendedNotification[]
  loading: boolean
  search: string
  filter: NotificationFilter
}

export const NOTIFICATIONS_CENTER_INITIAL_STATE: NotificationsCenterState = {
  notifications: [],
  loading: false,
  search: '',
  filter: DEFAULT_NOTIFICATION_FILTER,
}

const markRead = (
  notifications: ExtendedNotification[],
  notificationIds: string[]
): ExtendedNotification[] => {
  const readIds = new Set(notificationIds)

  return notifications.map((notification) =>
    readIds.has(notification.id) ? { ...notification, markedRead: true } : notification
  )
}

/**
 * The notification center's list: everything the history endpoint returned, plus every frame that
 * has arrived over SSE since, one entry per notification id.
 *
 * Root-provided because the bell lives in the app chrome, outlives every route, and has to keep
 * accumulating while the panel is closed. Deliberately separate from `NotificationsService`, which
 * is a point-in-time toast engine — portal host, visible cap, pause protocol — with no notion of
 * history or read state.
 */
export const NotificationsCenterStore = signalStore(
  { providedIn: 'root' },
  withState(NOTIFICATIONS_CENTER_INITIAL_STATE),

  withProps(() => ({
    _notificationsCenterApi: inject(NotificationsCenterApi),
    _toastMessageService: inject(ToastMessageService),
  })),

  withComputed((store) => ({
    unreadCount: computed(
      () => store.notifications().filter(({ markedRead }) => !markedRead).length
    ),
    isSearching: computed(() => store.search().trim().length >= MIN_SEARCH_LENGTH),
    isFiltering: computed(() => store.filter() !== DEFAULT_NOTIFICATION_FILTER),
    filtered: computed(() =>
      searchAndFilterNotifications(store.notifications(), store.search(), store.filter())
    ),
  })),

  withMethods((store) => {
    const load = async (): Promise<void> => {
      patchState(store, { loading: true })

      try {
        const notifications = await lastValueFrom(store._notificationsCenterApi.getNotifications())
        patchState(store, {
          loading: false,
          notifications: notifications.map(extendNotification),
        })
      } catch (error) {
        patchState(store, { loading: false })
        console.error('[Notifications center] Failed to load the notification history', error)
      }
    }

    const applyRead = (notificationIds: string[]): void =>
      patchState(store, (state) => ({
        notifications: markRead(state.notifications, notificationIds),
      }))

    return {
      load,

      // A repeat of a notification already held (an export reports when it starts and again when it
      // finishes, under one id) replaces it and moves to the top, instead of being listed twice.
      add: (notification: ExtendedNotification): void => {
        patchState(store, (state) => ({
          notifications: [
            notification,
            ...state.notifications.filter(({ id }) => id !== notification.id),
          ],
        }))
      },

      markAsRead: async (notificationId: string): Promise<void> => {
        try {
          await lastValueFrom(store._notificationsCenterApi.markAsRead(notificationId))

          applyRead([notificationId])
        } catch (error) {
          store._toastMessageService.showErrorMessage('Could not mark the notification as read')
        }
      },

      markAllAsRead: async (): Promise<void> => {
        // Captured before the request, because this tab's own SSE connection keeps delivering while
        // it is in flight. A notification that arrives in that window was never part of the
        // mark-all the server ran, so marking it read here would hide a genuinely new notification
        // behind an already-read badge.
        const unreadIds = store
          .notifications()
          .filter(({ markedRead }) => !markedRead)
          .map(({ id }) => id)

        try {
          await lastValueFrom(store._notificationsCenterApi.markAllAsRead())

          applyRead(unreadIds)
          store._toastMessageService.showSuccessMessage('All notifications marked as read')
        } catch (error) {
          store._toastMessageService.showErrorMessage('Could not mark all notifications as read')
        }
      },

      /**
       * A read the server reported over the stream — from another tab, another device, or this tab
       * echoing back a read it has already applied against its own 200, which `markRead` absorbs.
       *
       * No id is the mark-all, and means everything currently held. See `NotificationReadEvent` for
       * why that is safe to take literally.
       */
      applyServerRead: (notificationId?: string | null): void =>
        patchState(store, (state) => ({
          notifications: notificationId
            ? markRead(state.notifications, [notificationId])
            : state.notifications.map((notification) => ({ ...notification, markedRead: true })),
        })),

      setSearch: (search: string): void => {
        patchState(store, { search })
      },

      setFilter: (filter: NotificationFilter): void => {
        patchState(store, { filter })
      },
    }
  })
)
